import React, { useEffect, useState } from 'react';
import { Button } from '@lognai/synapse-design-system-v2';
import { useUndoAiDraftItem } from '../../hooks/use-bundle-mutations';

export interface UndoBannerProps {
  draftId: string;
  bundleId?: string;       // for query invalidation hints on the mutation
  deadlineAt: Date;        // when undo expires
  label?: string;          // optional text override; default: "Applied a change"
  onDismiss?: () => void;  // called when banner self-dismisses or undo completes
}

export const UndoBanner: React.FC<UndoBannerProps> = ({
  draftId, bundleId, deadlineAt, label = 'Applied a change', onDismiss,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const ms = deadlineAt.getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 1000));
  });
  const undo = useUndoAiDraftItem(bundleId);
  const [undoing, setUndoing] = useState(false);
  const [undone, setUndone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (secondsLeft <= 0) {
      onDismiss?.();
      return undefined;
    }
    const t = setInterval(() => {
      setSecondsLeft((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(t);
          onDismiss?.();
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [secondsLeft, onDismiss]);

  const handleUndo = async () => {
    setUndoing(true);
    setError(null);
    try {
      await undo.mutateAsync(draftId);
      setUndone(true);
      onDismiss?.();
    } catch (e) {
      // Chain-dependent undo: surface the specific blocking error inline so the
      // user can act on it. Other errors render a generic message.
      const code = (e as { code?: string; details?: { code?: string } })?.code
        ?? (e as { details?: { code?: string } })?.details?.code;
      if (code === 'PREDECESSOR_HAS_LIVE_DEPENDENT') {
        const ids = ((e as { details?: { dependentDraftIds?: string[] } })?.details?.dependentDraftIds) ?? [];
        setError(
          ids.length > 0
            ? `Can't undo yet — draft ${ids.join(', ')} references this. Undo it first.`
            : `Can't undo yet — a dependent draft references this. Undo it first.`,
        );
      } else {
        setError((e as Error)?.message ?? 'Undo failed.');
      }
      setUndoing(false);
    }
  };

  if (undone) return null;

  const expired = secondsLeft <= 0;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'sticky',
        bottom: 0,
        background: 'var(--synapse-surface, #fff)',
        border: '1px solid var(--synapse-border, #e5e7eb)',
        borderRadius: 6,
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginTop: 8,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 13 }}>
          ✓ {label}{expired ? '' : ` · ${secondsLeft}s`}
        </span>
        {error && (
          <span role="alert" style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>
            {error}
          </span>
        )}
      </div>
      {!expired && (
        <Button size="sm" variant="ghost" onClick={handleUndo} disabled={undoing}>
          {undoing ? 'Undoing…' : 'Undo'}
        </Button>
      )}
    </div>
  );
};
